import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { PageDataFetchError } from "@looksrare/utils";
import { CurrentRoundView, getCurrentRound, Round, YoloContractName } from "@looksrare/yolo";
import { headers } from "@/server";
import { getCanonicalAndLanguageAlternates } from "@/utils/seo";
import { Page } from "@/components";
import { YoloGamesYoloProvider } from "@/components/YoloGamesYoloProvider";
import { getChainInfo } from "@/utils";

const path = "/yolo";

const YoloPage = ({ round }: { round: Round }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentChainInfo = getChainInfo();

  const title = "YOLO";
  const description =
    "Go big or go home in YOLO — the heart-pounding, winner-takes-all battle royale. Play with crypto on YOLO Games today!";
  const { canonical, languageAlternates } = getCanonicalAndLanguageAlternates(path, router, currentChainInfo.appUrl);

  return (
    <Page title={title}>
      <NextSeo
        noindex
        nofollow
        title={title}
        description={description}
        openGraph={{
          url: canonical,
          title: t("{{pageTitle}} | YOLO Games", { pageTitle: title }),
          description: description,
          images: [
            {
              url: `${currentChainInfo.appUrl}/images/yolo/meta.png`,
              alt: title,
              width: 1600,
              height: 840,
              type: "image/png",
            },
          ],
        }}
        canonical={canonical}
        languageAlternates={languageAlternates}
      />

      <YoloGamesYoloProvider>
        <CurrentRoundView
          round={round}
          key={
            // Force re-render when round or network changes
            `${round.contract}-${round.onChainId}`
          }
        />
      </YoloGamesYoloProvider>
    </Page>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ res, locale = "en" }) => {
  res.setHeader("Cache-Control", "public, max-age=1"); // No Vercel edge cache
  try {
    const round = await getCurrentRound([YoloContractName.YOLO_V2_BLAST], headers);
    return {
      props: {
        ...(await serverSideTranslations(locale, ["yolo", "common"])),
        round,
      },
    };
  } catch (e) {
    console.error(e);
    throw new PageDataFetchError(path, e);
  }
};

export default YoloPage;
